import { useEffect, useMemo, useState } from 'react'
import { useDisclosure, Heading, Link, Text, Divider } from '@ritualco/jutsu'
import { useUpdateWorkspaceAdmin } from 'api/endpoints/portalAdmins'
import { PortalAdminRole, WorkspacePortalAdmin } from 'lib/generated/api'
import { useContent } from 'lib/content/react'
import {
  EditableSection,
  EditableSectionTerm,
  EditableSectionTermRow,
  EditableSectionValue,
} from 'components/EditableSection'
import { LocaleOption, ProfileModal } from 'components/Modals/ProfileModal'
import { MaxWidthPage } from 'components/PageTemplates'
import { selectUserInfo } from 'state/selectors/userInfo'
import { useStore } from 'state/store'
import { Locale, LOCALES } from 'types/locale'
import Loading from 'components/Loading'

type ProfileProps = {
  organizationId: string
  workspaceId: string
}

const Profile = ({ workspaceId, organizationId }: ProfileProps) => {
  const { formatMessage, setLocale } = useContent()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const update = useUpdateWorkspaceAdmin()
  const { firstName, email, locale, role, portalAdminId } = useStore(
    selectUserInfo
  )

  const passwordManagementUrl = `${
    process.env.REACT_APP_KEYCLOAK_PASSWORD_MANAGE_URL
  }?referrer=${
    process.env.REACT_APP_KEYCLOAK_AUTH_CLIENTID
  }&referrer_uri=${window.location.toString()}`

  const localeLabels = useMemo<Record<Locale, string>>(
    () => ({
      'en-AU': formatMessage({ id: 'pmportal.global.englishAU' }),
      'en-CA': formatMessage({ id: 'pmportal.global.englishCA' }),
      'en-GB': formatMessage({ id: 'pmportal.global.englishGB' }),
      'en-US': formatMessage({ id: 'pmportal.global.englishUS' }),
      'fr-CA': formatMessage({ id: 'pmportal.global.frenchCA' }),
      'zh-HK': formatMessage({ id: 'pmportal.global.chineseHK' }),
    }),
    [formatMessage]
  )

  if (!portalAdminId || !email || !role) {
    return <Loading />
  }

  const localeOptions: LocaleOption[] = LOCALES.map(value => ({
    label: localeLabels[value],
    value,
  }))

  return (
    <MaxWidthPage
      heading={
        <Heading fontSize="2xlarge">
          {formatMessage({ id: 'pmportal.settings.profile.title' })}
        </Heading>
      }
      showDivider={false}
    >
      <Text fontSize="small" pb={3}>
        {formatMessage({ id: 'pmportal.settings.profile.description' })}
      </Text>
      <Divider />
      <EditableSection title="Profile Details" onEditClick={onOpen} pt={11}>
        <EditableSectionTermRow>
          <EditableSectionTerm>
            {formatMessage({ id: 'pmportal.settings.profile.firstNameLabel' })}
          </EditableSectionTerm>
          <EditableSectionValue>{firstName || '-'}</EditableSectionValue>
        </EditableSectionTermRow>
        <EditableSectionTermRow>
          <EditableSectionTerm>
            {formatMessage({ id: 'pmportal.settings.profile.emailLabel' })}
          </EditableSectionTerm>
          <EditableSectionValue>{email}</EditableSectionValue>
        </EditableSectionTermRow>
        <EditableSectionTermRow>
          <EditableSectionTerm>
            {formatMessage({ id: 'pmportal.settings.profile.languageLabel' })}
          </EditableSectionTerm>
          <EditableSectionValue>
            {locale ? localeLabels[locale] : ''}
          </EditableSectionValue>
        </EditableSectionTermRow>
        <EditableSectionTermRow>
          <EditableSectionTerm>
            {formatMessage({ id: 'pmportal.settings.profile.passwordLabel' })}
          </EditableSectionTerm>
          <EditableSectionValue>
            <Link fontWeight="medium" href={passwordManagementUrl}>
              {formatMessage({
                id: 'pmportal.settings.profile.changePassword',
              })}
            </Link>
          </EditableSectionValue>
        </EditableSectionTermRow>
      </EditableSection>
      <ProfileModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={({ firstName, locale }) => {
          update.mutate(
            {
              organizationId,
              workspaceId,
              portalAdminId,
              portalAdmin: {
                email,
                role: role as PortalAdminRole,
                firstName,
                locale,
              },
            },
            {
              onSuccess: () => setLocale(locale),
            }
          )
        }}
        firstName={firstName}
        email={email}
        locale={locale}
        localeOptions={localeOptions}
      />
    </MaxWidthPage>
  )
}

export default Profile
